import React, { memo, useImperativeHandle,useMemo, useRef, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, MenuProps, styled, Typography } from '@mui/material';

import {
  LogoMenuIcon,
  LogoMenuSubitemUpdateIcon,
  LogoStudioSelectPlaceholder,
  TrashBinMenuIcon,
} from '../../Icons';
import { convertToBase64 } from '../../utils';

const ActionButton = styled(IconButton)({
  position: 'absolute',
  right: '4px',
  bottom: '24px',
  width: '32px',
  height: '32px',
  backgroundColor: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
  color: '#676767',
  fontSize: '16px',
  fontWeight: '400',
  ':hover': {
    backgroundColor: '#FAFAFA',
  },

  ':active': {
    boxShadow: 'none',
    border: '1px solid #F3F3F3',
    backgroundColor: '#FAFAFA',
  }
});

const ActionMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    {...props}
  />
))(({theme}) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: 8,
    minWidth: 120,
    color: '#363636',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '0',
    },
    '& .MuiMenuItem-root': {
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '400',
      minHeight: '38px',

      '& .MuiSvgIcon-root': {
        fontSize: 16,
        marginRight: 8,
      },
      '&:active, &:hover': {
        backgroundColor: '#BF295C',
        color: 'white',
      },
    },
  },
}));

enum ActionType {
  update,
  delete,
}

interface FloatingImageButtonProps {
  onUpdateClick: () => void,
  onDeleteClick: () => void,
}

function FloatingImageButton({
  onUpdateClick,
  onDeleteClick,
}: FloatingImageButtonProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: ActionType) => {
    return () => {
      switch (action) {
        case ActionType.update:
          onUpdateClick?.();
          console.log('should update');
          break;
        case ActionType.delete:
          onDeleteClick?.();
          console.log('should delete');
          break;
        default:
          throw new Error('Not proper ActionType');
      }
      setAnchorEl(null);
    };
  };

  return (
    <div>
      <ActionButton
        id="action-button"
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        disableRipple
        sx={{
          ...(open ? {
            backgroundColor: open ? '#f3f3f3' : undefined,
            boxShadow: 'none',
            border: '1px solid #F3F3F3',
          } : {})
        }}
      >
        <LogoMenuIcon fontSize="inherit"/>
      </ActionButton>
      <ActionMenu
        id="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'action-button',
        }}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
      >
        <MenuItem onClick={handleAction(ActionType.update)} disableRipple>
          <LogoMenuSubitemUpdateIcon fontSize="inherit"/>
          Update photo
        </MenuItem>
        <MenuItem onClick={handleAction(ActionType.delete)} disableRipple>
          <TrashBinMenuIcon/>
          Delete
        </MenuItem>
      </ActionMenu>
    </div>
  );
}

interface LogoStudioSelectProps {
  onImageChanged: (image: string) => void,
  image: string,
  ref: any,
}

const LogoStudioSelect = memo(React.forwardRef(({
  onImageChanged,
  image,
}: LogoStudioSelectProps, ref) => {

  const placeholderImageSrc = '/images/select-avatar-placeholder.png';

  const [imageSrc, setImageSrc] = useState<string>(image || placeholderImageSrc);

  const isImageWasAdded = useMemo(() => {
    return imageSrc !== placeholderImageSrc;
  }, [imageSrc]);

  const fileInputRef = useRef<any>(null);
  const formRef = useRef<any>(null);

  const handleUploadImageButton = () => {
    fileInputRef?.current?.click();
  };

  const handleDeleteImage = () => {
    formRef?.current?.reset();
    setImageSrc(placeholderImageSrc);
    onImageChanged('');
  };

  const handleImageChange = async (e: any) => {
    if (e.target.files) {
      const base64 = await convertToBase64(e.target.files[0]);

      setImageSrc(base64);
      onImageChanged(base64);
    }
  };

  useImperativeHandle(ref, () => ({
    deleteImage: handleDeleteImage
  }));

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      width: 146,
      height: 146,
      top: '-73px',
    }}>
      <Box
        sx={{
          width: 146,
          height: 146,
          borderRadius: '50%',
          backgroundColor: '#F3F3F3',
          cursor: !isImageWasAdded ? 'pointer' : undefined,
          border: '8px solid white',
          display: 'flex',
        }}
        onClick={!isImageWasAdded ? handleUploadImageButton : undefined}
      >
        {
          isImageWasAdded ? (
            <img
              src={image}
              style={{
                width: 130,
                height: 130,
                objectFit: 'fill',
                borderRadius: '50%',
                top: '-5px'
              }}
            />) : (
            <Box style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              color: '#D6D6DB',
              fontSize: '50px',
            }}>
              <LogoStudioSelectPlaceholder fontSize="inherit"/>
              <Typography sx={{
                color: '#BF295C',
                fontWeight: '400',
                fontSize: '13px',
              }}>
                Upload logo
              </Typography>
            </Box>
          )
        }
      </Box>
      <form ref={formRef}>
        <input
          ref={fileInputRef}
          onChange={handleImageChange}
          style={{
            display: 'none'
          }}
          type="file"
          accept="image/*"
        />
        {
          isImageWasAdded ? (<FloatingImageButton
            onUpdateClick={handleUploadImageButton}
            onDeleteClick={handleDeleteImage}
          />
          ) : null
        }
      </form>
    </Box>
  );
}));

export {LogoStudioSelect}
